import api from './api';

export type PublicOutreachStatus =
  | 'draft'
  | 'researching'
  | 'collecting'
  | 'ready'
  | 'active'
  | 'completed'
  | 'failed';

export interface PublicOutreachCampaign {
  id: string;
  name: string;
  description?: string;
  targetIndustries?: string[];
  targetRegion: string;
  targetCriteria?: {
    nearLocations?: string;
    specificLocations?: string[];
    additionalCriteria?: string;
  };
  goal?: string;
  language?: string;
  legalBasisNote?: string;
  lemlistCampaignId?: string | null;
  lemlistPushedAt?: string | null;
  status: PublicOutreachStatus;
  icpId?: string | null;
  userId: string;
  createdAt: string;
  updatedAt: string;
  targets?: PublicCompanyTarget[];
  _count?: { targets: number };
}

export interface PublicCompanyTarget {
  id: string;
  campaignId: string;
  companyName: string;
  domain?: string | null;
  website?: string | null;
  industry?: string | null;
  city?: string | null;
  region?: string | null;
  publicEmail?: string | null;
  emailType?: string | null;
  source: string;
  sourceUrl?: string | null;
  collectedAt?: string | null;
  status: string;
  optOut: boolean;
  lemlistLeadId?: string | null;
  icebreakerEmail?: string | null;
  icebreakerSubject?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePublicOutreachInput {
  name: string;
  description?: string;
  targetIndustries?: string[];
  targetRegion: string;
  targetCriteria?: {
    nearLocations?: string;
    specificLocations?: string[];
    additionalCriteria?: string;
  };
  goal?: string;
  language?: string;
  legalBasisNote?: string;
  icpId?: string;
}

export interface CollectResult {
  scanned: number;
  found: number;
  suppressed: number;
  failed: number;
  details: Array<{ company: string; email?: string; sourceUrl?: string; success: boolean; error?: string }>;
}

export interface PushResult {
  pushed: number;
  skipped: number;
  failed: number;
  dryRun: boolean;
  details: Array<{ company: string; email?: string; success: boolean; skipped?: boolean; error?: string }>;
}

export interface Suppression {
  id: string;
  email: string;
  reason?: string;
  source?: string;
  createdAt: string;
}

export type ImportSource = 'contactlead' | 'ausschreibungsmatcher' | 'visitor-reveal' | 'manual';

class PublicOutreachService {
  async getCampaigns(status?: string): Promise<PublicOutreachCampaign[]> {
    const res = await api.get('/public-outreach/campaigns', { params: status ? { status } : {} });
    return res.data;
  }

  async getCampaign(id: string): Promise<PublicOutreachCampaign> {
    const res = await api.get(`/public-outreach/campaigns/${id}`);
    return res.data;
  }

  async createCampaign(input: CreatePublicOutreachInput): Promise<PublicOutreachCampaign> {
    const res = await api.post('/public-outreach/campaigns', input);
    return res.data;
  }

  async updateCampaign(id: string, input: Partial<CreatePublicOutreachInput & { status: string }>) {
    const res = await api.put(`/public-outreach/campaigns/${id}`, input);
    return res.data as PublicOutreachCampaign;
  }

  async deleteCampaign(id: string): Promise<void> {
    await api.delete(`/public-outreach/campaigns/${id}`);
  }

  async research(id: string): Promise<{ targetsCreated: number; summary?: string }> {
    const res = await api.post(`/public-outreach/campaigns/${id}/research`);
    return res.data;
  }

  async importSource(id: string, source: ImportSource, companies?: any[]): Promise<{ imported: number }> {
    const res = await api.post(`/public-outreach/campaigns/${id}/import`, { source, companies });
    return res.data;
  }

  async collect(id: string): Promise<CollectResult> {
    const res = await api.post(`/public-outreach/campaigns/${id}/collect`);
    return res.data;
  }

  async push(id: string, opts: { lemlistCampaignId?: string; createCampaign?: boolean }): Promise<PushResult> {
    const res = await api.post(`/public-outreach/campaigns/${id}/push`, opts);
    return res.data;
  }

  async getTargets(id: string): Promise<PublicCompanyTarget[]> {
    const res = await api.get(`/public-outreach/campaigns/${id}/targets`);
    return res.data;
  }

  async updateTarget(id: string, input: Partial<PublicCompanyTarget>): Promise<PublicCompanyTarget> {
    const res = await api.put(`/public-outreach/targets/${id}`, input);
    return res.data;
  }

  async deleteTarget(id: string): Promise<void> {
    await api.delete(`/public-outreach/targets/${id}`);
  }

  async exportTargets(id: string, format: 'json' | 'csv' = 'csv'): Promise<any> {
    const res = await api.get(`/public-outreach/campaigns/${id}/export`, {
      params: { format },
      responseType: format === 'csv' ? 'blob' : 'json',
    });
    return res.data;
  }

  async listSuppressions(): Promise<Suppression[]> {
    const res = await api.get('/public-outreach/suppressions');
    return res.data;
  }

  async addSuppression(email: string, reason?: string): Promise<Suppression> {
    const res = await api.post('/public-outreach/suppressions', { email, reason });
    return res.data;
  }

  async removeSuppression(id: string): Promise<void> {
    await api.delete(`/public-outreach/suppressions/${id}`);
  }

  downloadCSV(data: Blob, filename: string) {
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

const publicOutreachService = new PublicOutreachService();
export default publicOutreachService;
